import { useRef, useState } from 'react';
import { msg } from '@gonfalon/messages/msg';
import {
  Alert,
  ComboBox,
  ComboBoxClearButton,
  Group,
  Input,
  ListBox,
  ListBoxItem,
  Popover,
  ProgressBar,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import type { AggregationType, DimensionType, Granularity, MeterType } from '../../../../types';
import type { DataExportBreakdown } from '../../../hooks/useDataExportBreakdown';
import type { useUsageTimestamps } from '../../../hooks/useUsageTimestamps';
import { BREAKDOWN_TOP_N, type BreakdownGroup } from '../../../utils/dataExportBreakdown';
import { getMaxYValue } from '../../charts/UsageDetailChart';

import { DataExportBreakdownChartCard } from './DataExportBreakdownChartCard';

import detailStyles from '../UsageDetail.module.css';
import styles from './UsageDetailGrouped.module.css';

type DataExportBreakdownChartsProps = {
  breakdown: DataExportBreakdown;
  dimension: DimensionType;
  aggregationType: AggregationType;
  timestamps: ReturnType<typeof useUsageTimestamps>;
  abbreviatedName: string;
  meterType: MeterType;
  granularity: Granularity;
  precision?: number;
  trialEndUTC?: number;
  seriesColor?: string;
};

export function DataExportBreakdownCharts({ breakdown, precision, ...cardProps }: DataExportBreakdownChartsProps) {
  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { view, charts } = breakdown;

  if (view.status === 'loading' || (view.status === 'ready' && charts.status === 'loading')) {
    return <ProgressBar aria-label={msg('billing.usageBreakdown.loadingLabel')} isIndeterminate size="medium" />;
  }
  if (view.status !== 'ready' || view.ranked.length === 0) {
    return null;
  }
  if (charts.status !== 'ready') {
    return <Alert status="error">{msg('billing.usageBreakdown.errorLoadingDimension')}</Alert>;
  }

  const term = search.trim().toLowerCase();
  const matches = view.ranked.filter(({ label }) => label.toLowerCase().includes(term));
  const selected = view.ranked.find(({ key, label }) => key === selectedKey && label === search);
  const shown = (selected ? [selected] : matches)
    .slice(0, BREAKDOWN_TOP_N)
    .map((group) => ({ ...group, data: charts.byKey[group.key] }));
  const searchLabel = msg('billing.dataExportCombined.searchAllPlaceholder', { count: view.ranked.length });
  const yAxisMax = getMaxYValue(
    shown.flatMap(({ data }) => data.series),
    precision,
  );

  return (
    <div>
      <ComboBox
        className={detailStyles.searchField}
        aria-label={searchLabel}
        allowsCustomValue
        menuTrigger="input"
        items={matches}
        inputValue={search}
        onInputChange={(value) => {
          setSearch(value);
          if (selectedKey !== null && value !== view.ranked.find(({ key }) => key === selectedKey)?.label) {
            setSelectedKey(null);
          }
        }}
        selectedKey={selectedKey}
        onSelectionChange={(key) => {
          const group = key === null ? undefined : view.ranked.find((ranked) => ranked.key === String(key));
          setSelectedKey(group?.key ?? null);
          if (group) {
            setSearch(group.label);
          }
        }}
      >
        <Group>
          <Icon name="search" size="small" />
          <Input ref={inputRef} placeholder={searchLabel} />
          {search && (
            <ComboBoxClearButton
              aria-label={msg('billing.usageDetail.clearSearchLabel')}
              onPressEnd={() => {
                setSearch('');
                setSelectedKey(null);
                requestAnimationFrame(() => inputRef.current?.focus());
              }}
            />
          )}
        </Group>
        <Popover>
          <ListBox>
            {(group: BreakdownGroup) => (
              <ListBoxItem id={group.key} textValue={group.label}>
                {group.label}
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </ComboBox>
      {shown.length === 0 ? (
        <p className={styles.emptyStateDescription}>{msg('billing.usageBreakdown.noSearchMatches')}</p>
      ) : (
        <div className={styles.cardGrid}>
          {shown.map(({ key, label, data }) => (
            <DataExportBreakdownChartCard
              key={key}
              label={label}
              data={data}
              yAxisMax={yAxisMax}
              precision={precision}
              {...cardProps}
            />
          ))}
        </div>
      )}
    </div>
  );
}
